import { useMutation, useQuery, UseQueryOptions } from "react-query";
import HttpService from "../../services/http";
import { AxiosError } from "axios";
import { queryClient } from "../../providers/QueryProvider";

export interface TrialRestrictions {
  messaging_disabled: boolean;
  sms_disabled: boolean;
  api_disabled: boolean;
}

export interface TrialInfo {
  code: string;
  plan: string;
  days_left: number;
  end_date?: string;
  restrictions: TrialRestrictions;
}

export interface ApplyPromoCodeResponse {
  status: string;
  message: string;
  element: {
    trial_info: TrialInfo;
  };
}

export interface GetTrialStatusResponse {
  status: string;
  message: string;
  data: {
    has_active_trial: boolean;
    trial_info: TrialInfo | null;
  };
}

export const useGetTrialStatus = (
  customerIdentification: string,
  entityIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["trial-status", customerIdentification, entityIdentification],
    async () => {
      const res = await HttpService.get(
        `/${customerIdentification}/${entityIdentification}/promo-code/status`
      );

      const apiResponse = res?.data;
      return {
        data: apiResponse?.element || null,
      };
    },
    {
      enabled: !!customerIdentification && !!entityIdentification,
      staleTime: 30 * 1000,
      cacheTime: 5 * 60 * 1000,
      ...options,
    }
  );
};

export const useApplyPromoCode = () => {
  return useMutation<
    ApplyPromoCodeResponse,
    AxiosError<any, any>,
    {
      customerIdentification: string;
      entityIdentification: string;
      promoCode: string;
    }
  >(
    async ({ customerIdentification, entityIdentification, promoCode }) => {
      const res = await HttpService.post(
        `/${customerIdentification}/${entityIdentification}/promo-code/apply`,
        { promo_code: promoCode }
      );
      return res?.data;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "trial-status",
          variables.customerIdentification,
          variables.entityIdentification,
        ]);
        queryClient.invalidateQueries("owner-plans");
        queryClient.invalidateQueries("verify-plans");
      },
    }
  );
};
